import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import animeConfig from '../config/animeConfig';
import characterProfiles from '../data/characterProfiles';
import './Guides.css';
import './CharacterProfile.css';
import './ArcsPage.css';

// Hardcoded slugs for categories that can't be matched by crew/role text alone
const WORST_GEN_SLUGS = [
  'monkey-d-luffy', 'roronoa-zoro', 'trafalgar-law', 'eustass-kid', 'killer',
  'jewelry-bonney', 'basil-hawkins', 'capone-bege', 'scratchmen-apoo', 'urouge',
  'x-drake', 'blackbeard',
];
const EMPEROR_SLUGS = [
  'shanks', 'blackbeard', 'monkey-d-luffy', 'buggy', // current
  'big-mom', 'kaido', 'edward-newgate', // former
];
const WARLORD_SLUGS = [
  'dracule-mihawk', 'boa-hancock', 'crocodile', 'donquixote-doflamingo',
  'bartholomew-kuma', 'gecko-moria', 'buggy', 'trafalgar-law', 'jinbe',
  'blackbeard', // former warlord
];
const MARINE_SLUGS = [
  'monkey-d-garp', 'smoker', 'tashigi', 'sakazuki-akainu', 'borsalino-kizaru',
  'kuzan-aokiji', 'fujitora', 'sengoku', 'coby', 'helmeppo', 'sentomaru',
  'donquixote-rosinante', 'vergo', 'x-drake', 'hina', 'magellan', 'hannyabal',
];

const ROGER_PIRATES_SLUGS = [
  'gol-d-roger', 'silvers-rayleigh', 'shanks', 'buggy',
];
const CP_SLUGS = [
  'rob-lucci', 'kaku', 'jabra', 'kalifa', 'kumadori', 'spandam',
];
const SKYPIEA_SLUGS = [
  'enel', 'gan-fall', 'wiper', 'calgara',
];
const IMPEL_DOWN_SLUGS = [
  'magellan', 'hannyabal',
];
const GRAND_FLEET_SLUGS = [
  'bartolomeo', 'cavendish',
];
const VINSMOKE_SLUGS = [
  'vinsmoke-sanji', 'vinsmoke-reiju', 'vinsmoke-judge',
];

const CREW_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'straw-hat', label: 'Straw Hats', match: 'Straw Hat Pirates' },
  { id: 'emperor', label: 'Emperors', slugs: EMPEROR_SLUGS },
  { id: 'marine', label: 'Marines', slugs: MARINE_SLUGS },
  { id: 'warlord', label: 'Warlords', slugs: WARLORD_SLUGS },
  { id: 'worst-gen', label: 'Worst Gen', slugs: WORST_GEN_SLUGS },
  { id: 'roger', label: 'Roger Pirates', slugs: ROGER_PIRATES_SLUGS },
  { id: 'cp', label: 'CP9 / CP0', slugs: CP_SLUGS },
  { id: 'revolutionary', label: 'Revolutionary', match: 'Revolutionary Army' },
  { id: 'villain', label: 'Villains', match: ['Donquixote Pirates', 'Baroque Works', 'CP9', 'CP0', 'Arlong Pirates', 'Thriller Bark', 'Beasts Pirates', 'Big Mom Pirates'] },
  { id: 'wano', label: 'Wano', match: ['Nine Red Scabbards', 'Kozuki', 'Mokomo', 'Mink'] },
  { id: 'skypiea', label: 'Skypiea', slugs: SKYPIEA_SLUGS },
  { id: 'impel', label: 'Impel Down', slugs: IMPEL_DOWN_SLUGS },
  { id: 'fleet', label: 'Grand Fleet', slugs: GRAND_FLEET_SLUGS },
  { id: 'vinsmoke', label: 'Vinsmoke', slugs: VINSMOKE_SLUGS },
];

const CharactersIndex = () => {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  const bgStyle = {
    backgroundImage: `linear-gradient(to bottom, rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0.85)), url(${animeConfig.onePiece.backgroundImage})`,
  };

  const filtered = useMemo(() => {
    let chars = characterProfiles;

    // Search — always searches ALL characters regardless of active filter
    if (search.trim()) {
      const q = search.toLowerCase();
      // Reset filter when searching so results aren't restricted
      chars = characterProfiles.filter(c =>
        c.name.toLowerCase().includes(q) ||
        (c.aliases && c.aliases.some(a => a.toLowerCase().includes(q))) ||
        (c.crew && c.crew.toLowerCase().includes(q)) ||
        (c.role && c.role.toLowerCase().includes(q)) ||
        (c.devilFruit && c.devilFruit.toLowerCase().includes(q)) ||
        (c.bio && c.bio.toLowerCase().includes(q))
      );
      return chars;
    }

    // Crew filter (only when not searching)
    if (filter !== 'all') {
      const f = CREW_FILTERS.find(x => x.id === filter);
      if (f) {
        if (f.slugs) {
          // Use hardcoded slug list for accurate filtering
          chars = chars.filter(c => f.slugs.includes(c.slug));
        } else if (f.match) {
          const matches = Array.isArray(f.match) ? f.match : [f.match];
          chars = chars.filter(c => {
            const crewRole = `${c.crew || ''} ${c.role || ''}`.toLowerCase();
            return matches.some(m => crewRole.includes(m.toLowerCase()));
          });
        }
      }
    }

    return chars;
  }, [search, filter]);

  return (
    <>
      <Helmet>
        <title>One Piece Archive - All Character Profiles</title>
        <meta name="description" content={`Browse ${characterProfiles.length} One Piece character profiles with Devil Fruits, bounties, crews, and key facts. Straw Hats, Emperors, Warlords, Marines and more.`} />
        <link rel="canonical" href="https://www.onepiecetrivia.com/characters" />
        <meta property="og:title" content="One Piece Characters - Browse All Profiles" />
        <meta property="og:description" content={`${characterProfiles.length} One Piece character profiles with images, facts, and stats.`} />
        <meta property="og:url" content="https://www.onepiecetrivia.com/characters" />
      </Helmet>

      <div className="guide-page-wrapper" style={bgStyle}>
        <div className="guide-container">
          <h1>One Piece Archive</h1>

          {/* Navigation tabs */}

          {/* Search */}
          <div className="archive-search">
            <input
              type="text"
              placeholder="Search by name, crew, or Devil Fruit..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="archive-search-input"
            />
            {search && (
              <button className="archive-search-clear" onClick={() => setSearch('')}>&times;</button>
            )}
          </div>

          {/* Crew filters */}
          <div className="archive-filters">
            {CREW_FILTERS.map(f => (
              <button
                key={f.id}
                className={`archive-filter-btn ${filter === f.id ? 'active' : ''}`}
                onClick={() => setFilter(filter === f.id ? 'all' : f.id)}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Results count */}
          <div className="archive-count">
            {filtered.length === characterProfiles.length
              ? `${characterProfiles.length} characters`
              : `${filtered.length} of ${characterProfiles.length}`
            }
          </div>

          <div className="char-grid">
            {filtered.map((char) => (
              <Link to={`/characters/${char.slug}`} key={char.slug} className="char-card">
                <img
                  src={char.image}
                  alt={`${char.name} from One Piece`}
                  className="char-card-image"
                  loading="lazy"
                />
                <div className="char-card-body">
                  <h3 className="char-card-name">{char.name}</h3>
                  <div className="char-card-meta">{char.role}</div>
                  {char.bounty && <div className="char-card-bounty">{char.bounty}</div>}
                </div>
              </Link>
            ))}
            {filtered.length === 0 && (
              <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px 0', color: 'rgba(255,255,255,0.5)' }}>
                No characters found
              </div>
            )}
          </div>

          <div className="guide-cta">
            <h2>Think you know them all?</h2>
            <p>Play any of the 3 quizzes and put your knowledge to the test.</p>
            <Link to="/" className="guide-cta-button">Play a Quiz</Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default CharactersIndex;
